import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useSearchParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import { MdContentCopy, MdCode, MdLink, MdCheckCircle } from 'react-icons/md';
import { toast } from 'react-toastify';

const IntegrationHelp = () => {
    const [searchParams] = useSearchParams();
    const offerId = searchParams.get('offerId');
    const initialTemplateId = searchParams.get('templateId');

    const [user, setUser] = useState<any>(null);
    const [offer, setOffer] = useState<any>({});
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId || '');
    const [activeTab, setActiveTab] = useState<'iframe' | 'link'>('iframe');

    const formLink = offer?._id && selectedTemplateId
        ? `${window.location.origin}/public/lead-form?offerId=${offer._id}&sellerId=${user?._id}&templateId=${selectedTemplateId}`
        : '';

    const iframeCode = formLink
        ? `<iframe src="${formLink}" style="width:100%;min-height:600px;border:none"></iframe>`
        : '';

    const pixelScriptCode = '<script src="https://api.leadrs.co/leadrs-pixel/pixel.js" async></script>';

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        ) {
            window.location.href = '/auth/sign-up';
        }
        if (offerId) {
            getOffer(offerId);
        }
        initUser();
        fetchTemplates();
        // eslint-disable-next-line
    }, [offerId]);

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/${id}`, config)
            .then((res) => {
                setOffer(res.data);
            })
            .catch((err) => {
                console.log('Error fetching offer', err);
            });
    };

    const fetchTemplates = async () => {
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            const response = await axios.get(`${CONFIG.apiUrl}/form-template`, config);
            setTemplates(response.data);

            if (!selectedTemplateId && response.data.length > 0) {
                const defaultTemplate = response.data.find((t: any) => t.isDefault);
                setSelectedTemplateId(defaultTemplate?._id || response.data[0]._id);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copie !`);
    };

    return (
        <div className="flex flex-col gap-5">
            <GoBack />

            {/* Header */}
            <div className="rounded-xl border border-leadrs-stroke bg-white px-6 py-6">
                <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">Vendeur</p>
                <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                    Integration du formulaire
                </h1>
                <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                    Integrez facilement votre formulaire de capture de leads sur votre site web
                </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Left Column */}
                <div className="space-y-5">
                    {/* Pixel Script */}
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-4 font-geist text-base font-bold text-leadrs-text">
                            1. Installer le pixel Leadrs
                        </h2>
                        <p className="mb-4 font-geist text-sm text-leadrs-text-soft">
                            Ajoutez ce script sur les pages de votre site ou vous integrez le formulaire.
                            Il permet a Leadrs de mesurer vos visiteurs et de detecter automatiquement votre secteur d'activite.
                        </p>
                        <div className="relative">
                            <textarea
                                value={pixelScriptCode}
                                readOnly
                                rows={2}
                                className="w-full resize-none rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 pr-24 font-mono text-xs text-leadrs-text"
                            />
                            <button
                                className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-2.5 py-1.5 font-geist text-xs text-white transition-opacity hover:opacity-80"
                                onClick={() => copyToClipboard(pixelScriptCode, 'Script pixel')}
                                type="button"
                            >
                                <MdContentCopy size={14} />
                                Copier
                            </button>
                        </div>
                    </div>

                    {/* Template Selection */}
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-4 font-geist text-base font-bold text-leadrs-text">
                            2. Choisir un template
                        </h2>
                        {templates.length > 0 ? (
                            <select
                                className="w-full rounded-lg border border-leadrs-stroke px-4 py-3 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                                value={selectedTemplateId}
                                onChange={(e) => setSelectedTemplateId(e.target.value)}
                            >
                                {templates.map((t) => (
                                    <option key={t._id} value={t._id}>
                                        {t.name}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className="rounded-xl border border-leadrs-stroke bg-leadrs-yellow-soft p-4">
                                <p className="mb-3 font-geist text-sm text-leadrs-text-soft">
                                    Aucun template disponible. Vous devez d'abord creer un template de formulaire.
                                </p>
                                <a
                                    href="/integrations"
                                    className="inline-flex items-center gap-2 rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                >
                                    Creer un template
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Integration Methods */}
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-4 font-geist text-base font-bold text-leadrs-text">
                            3. Methode d'integration
                        </h2>

                        {/* Tabs */}
                        <div className="mb-6 flex gap-2">
                            <button
                                onClick={() => setActiveTab('iframe')}
                                className={`flex items-center gap-2 rounded-lg px-3 py-1.5 font-geist text-sm transition-colors ${
                                    activeTab === 'iframe'
                                        ? 'bg-leadrs-brand text-white'
                                        : 'border border-leadrs-stroke text-leadrs-text-soft hover:border-leadrs-brand'
                                }`}
                            >
                                <MdCode size={16} />
                                Iframe (Recommande)
                            </button>
                            <button
                                onClick={() => setActiveTab('link')}
                                className={`flex items-center gap-2 rounded-lg px-3 py-1.5 font-geist text-sm transition-colors ${
                                    activeTab === 'link'
                                        ? 'bg-leadrs-brand text-white'
                                        : 'border border-leadrs-stroke text-leadrs-text-soft hover:border-leadrs-brand'
                                }`}
                            >
                                <MdLink size={16} />
                                Lien direct
                            </button>
                        </div>

                        {/* Iframe Tab */}
                        {activeTab === 'iframe' && formLink && (
                            <div className="space-y-4">
                                <div className="rounded-xl border border-leadrs-stroke bg-leadrs-blue-soft p-4">
                                    <h3 className="mb-2 flex items-center gap-2 font-geist text-sm font-bold text-leadrs-text">
                                        <MdCheckCircle className="text-leadrs-brand" />
                                        Comment ca marche ?
                                    </h3>
                                    <ol className="ml-2 list-inside list-decimal space-y-1 font-geist text-sm text-leadrs-text-soft">
                                        <li>Copiez le code iframe ci-dessous</li>
                                        <li>Collez-le dans le HTML de votre page</li>
                                        <li>Le formulaire s'affiche automatiquement</li>
                                    </ol>
                                </div>

                                <div>
                                    <label className="mb-2 block font-geist text-sm font-bold text-leadrs-text">
                                        Code a copier
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            value={iframeCode}
                                            readOnly
                                            rows={4}
                                            className="w-full rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 pr-24 font-mono text-xs text-leadrs-text"
                                        />
                                        <button
                                            className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-2.5 py-1.5 font-geist text-xs text-white transition-opacity hover:opacity-80"
                                            onClick={() => copyToClipboard(iframeCode, 'Code iframe')}
                                            type="button"
                                        >
                                            <MdContentCopy size={14} />
                                            Copier
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Link Tab */}
                        {activeTab === 'link' && formLink && (
                            <div className="space-y-4">
                                <div className="rounded-xl border border-leadrs-stroke bg-leadrs-green-soft p-4">
                                    <h3 className="mb-2 flex items-center gap-2 font-geist text-sm font-bold text-leadrs-text">
                                        <MdCheckCircle className="text-leadrs-green-focus" />
                                        Comment ca marche ?
                                    </h3>
                                    <ol className="ml-2 list-inside list-decimal space-y-1 font-geist text-sm text-leadrs-text-soft">
                                        <li>Copiez l'URL ci-dessous</li>
                                        <li>Utilisez-la dans un bouton ou menu</li>
                                        <li>Les visiteurs accedent au formulaire</li>
                                    </ol>
                                </div>

                                <div>
                                    <label className="mb-2 block font-geist text-sm font-bold text-leadrs-text">
                                        URL du formulaire
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={formLink}
                                            readOnly
                                            className="flex-1 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 font-geist text-sm text-leadrs-text"
                                        />
                                        <button
                                            className="flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-3 py-2.5 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                            onClick={() => copyToClipboard(formLink, 'Lien')}
                                            type="button"
                                        >
                                            <MdContentCopy size={16} />
                                            Copier
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Preview */}
                <div>
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-4 font-geist text-base font-bold text-leadrs-text">
                            Apercu du formulaire
                        </h2>
                        {formLink ? (
                            <div className="overflow-hidden rounded-lg border border-leadrs-stroke bg-white">
                                <iframe
                                    src={formLink}
                                    title="Formulaire de lead"
                                    style={{ width: '100%', height: '500px', border: 'none' }}
                                />
                            </div>
                        ) : (
                            <div className="flex h-[500px] items-center justify-center rounded-lg border-2 border-dashed border-leadrs-stroke">
                                <p className="font-geist text-sm text-leadrs-text-soft">
                                    Selectionnez un template pour voir l'apercu
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationHelp;
